import { Injectable } from '@angular/core';
import {
  Actions,
  createEffect,
  ofType,
  ROOT_EFFECTS_INIT,
} from '@ngrx/effects';
import { combineLatest, EMPTY } from 'rxjs';
import {
  map,
  catchError,
  switchMap,
  mergeMap,
  withLatestFrom,
} from 'rxjs/operators';
import { WavesConnectorService } from '@services/waves-connector.service';
import { PlutoOnWavesService } from '@services/pluto-on-waves.service';
import * as plutoActions from './pluto.actions';
import { Store } from '@ngrx/store';
import { selectPluto } from './pluto.selectors';
import * as notificationsActions from './notifications.actions';

@Injectable()
export class PlutoEffects {
  init$ = createEffect(() =>
    this._actions$.pipe(
      ofType(ROOT_EFFECTS_INIT),
      mergeMap(() => {
        this._wavesConnectorService.loginType;
        return [
          plutoActions.rehydrateData(),
          plutoActions.loadRehydratedDataToService(),
        ];
      }),
    ),
  );

  loadRehydratedDataToService$ = createEffect(() =>
    this._actions$.pipe(
      ofType(plutoActions.rehydrateData),
      withLatestFrom(this._store.select(selectPluto)),
      map(([_, { loginData }]) => {
        this._wavesConnectorService.updateLoginData(loginData);
        return plutoActions.loadRehydratedDataToServiceSuccess();
      }),
      catchError(() => EMPTY), // Add this line to handle any errors
    ),
  );

  loginOnLoginTypeChange$ = createEffect(() =>
    this._actions$.pipe(
      ofType(plutoActions.selectLoginType),
      map(({ loginType }) => plutoActions.login({ loginType })),
    ),
  );

  login$ = createEffect(() =>
    this._actions$.pipe(
      ofType(plutoActions.login),
      switchMap(({ loginType }) => {
        return this._wavesConnectorService.login(loginType).pipe(
          map(({ signer, loginData }) =>
            plutoActions.loginSuccess({ signer, loginData }),
          ),
          catchError(() => EMPTY),
        );
      }),
    ),
  );

  loginSuccess$ = createEffect(() =>
    this._actions$.pipe(
      ofType(plutoActions.loginSuccess),
      map(() => {
        return plutoActions.getBalances();
      }),
      mergeMap((action) => [
        action,
        plutoActions.hideLoginModal(),
        notificationsActions.success({ title: 'Login successful' }),
      ]),
    ),
  );

  logout$ = createEffect(() =>
    this._actions$.pipe(
      ofType(plutoActions.logout),
      switchMap(() => {
        return this._wavesConnectorService
          .logout()
          .pipe(map(() => plutoActions.logoutSuccess()));
      }),
    ),
  );

  getBalances$ = createEffect(() =>
    this._actions$.pipe(
      ofType(plutoActions.getBalances, ROOT_EFFECTS_INIT),
      switchMap(() => {
        this._wavesConnectorService.updateBalances();
        return combineLatest([
          this._plutoOnWavesService.getWalletBalance$(),
          this._plutoOnWavesService.getStakedBalance$(),
        ]).pipe(
          map(([wallet, stake]) =>
            plutoActions.getBalancesSuccess({
              wallet,
              stake,
            }),
          ),
        );
      }),
    ),
  );

  stake$ = createEffect(() =>
    this._actions$.pipe(
      ofType(plutoActions.stake),
      switchMap(async ({ value }) => {
        if (await this._plutoOnWavesService.stake(value))
          return plutoActions.stakeSuccess();
        return plutoActions.actionInProcess();
      }),
    ),
  );

  unstake$ = createEffect(() =>
    this._actions$.pipe(
      ofType(plutoActions.unstake),
      switchMap(async ({ value }) => {
        if (await this._plutoOnWavesService.unstake(value))
          return plutoActions.unstakeSuccess();
        return plutoActions.actionInProcess();
      }),
    ),
  );

  stakeUnstakeSended$ = createEffect(() =>
    this._actions$.pipe(
      ofType(plutoActions.stakeSended, plutoActions.unstakeSended),
      mergeMap(() => {
        return [
          plutoActions.hideStakeUnstakeModal(),
          notificationsActions.waiting({
            title: 'Your transaction was sent.',
            message: 'Wait until it is confirmed.',
          }),
        ];
      }),
    ),
  );

  stakeUnstakeSuccess$ = createEffect(() =>
    this._actions$.pipe(
      ofType(plutoActions.stakeSuccess, plutoActions.unstakeSuccess),
      mergeMap(() => {
        return [
          plutoActions.hideStakeUnstakeModal(),
          notificationsActions.success({ title: 'Transaction was succesful!' }),
        ];
      }),
    ),
  );

  balanceUpdate$ = createEffect(() =>
    this._actions$.pipe(
      ofType(plutoActions.stakeSuccess, plutoActions.unstakeSuccess),
      map(() => {
        return plutoActions.getBalances();
      }),
    ),
  );

  constructor(
    private _store: Store,
    private _actions$: Actions,
    private _wavesConnectorService: WavesConnectorService,
    private _plutoOnWavesService: PlutoOnWavesService,
  ) {}
}
